import { Link } from "gatsby"
import React, { useRef, useEffect } from "react"
import styled, { css } from "styled-components"
import { IconShakacodeDoc } from "styleguide/icons"
import { uiColorToBaseColor } from "styleguide/styles/colors"
import media from "styleguide/styles/media-queries"
import { indexPages, pageNametoLabel } from "src/libs/utils/toLabel"
import { fileOrderByRepo } from "src/libs/utils/fileOrder"
import { Articles } from "../../../types/docs"

const baseMenuOffset = 169

interface Props {
  repoName: string
  mdxName: string
  currentSlug: string
  categories: string[]
  articles: Articles
  isMobileMenu?: boolean
}

const CategoryTitle = styled.div`
  font-size: 14px;
  line-height: 24px;
  text-transform: uppercase;
  padding-bottom: 8px;
  border-bottom: 1px solid ${uiColorToBaseColor("lighterGray")};
  margin-top: 20px;
  margin-bottom: 16px;
  margin-left: 16px;
  margin-right: 32px;
  font-weight: 600;

  &:first-child {
    margin-top: 0;
  }

  ${media.tablet`
   margin-left: 16px;
  margin-right: 16px;
  `}
`

const ArticlesList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0 16px 0 32px;

  ${media.tablet`
    margin: 0 16px;
  `}
`

const ArticlesListItem = styled.li`
  margin: 5px 0;
  font-size: 14px;
  line-height: 22px;
  font-weight: 400;
`

const StyledLink = styled(Link)<{ isCurrent: boolean }>`
  color: ${uiColorToBaseColor("darkGray")} !important;
  position: relative;
  ${props =>
    props.isCurrent &&
    css`
      font-weight: bold !important;

      &::before {
        content: "";
        z-index: 1000;
        position: absolute;
        left: -15px;
        top: 50%;
        margin-top: -3px;
        height: 5px;
        width: 5px;
        border: 1px solid ${uiColorToBaseColor("red")};
        border-width: 2px 2px 0 0;
        transform: rotate(45deg);
      }
    `}

  &:hover {
    color: ${uiColorToBaseColor("blueDark")} !important;
    text-decoration: none;
  }
`

const Copyrights = styled.div`
  margin-top: 32px;
  margin-left: 32px;
  font-size: 14px;
  line-height: 24px;
  color: ${uiColorToBaseColor("darkestGray")};
`

const StyledIconShakacodeDoc = styled(IconShakacodeDoc)`
  height: 76px;
  width: 232px;
`

const DocMenu = styled.div<{ menuOffset: number }>`
  height: calc(100vh - ${props => props.menuOffset}px);
  overflow-y: scroll;
  padding-top: 48px;

  ${media.tablet`
    height: auto;
    padding-top:0;
    padding-bottom: 40px;
  `}
`

// Extract folder and article names from slug
// Slug format: "docs/upgrading/release-notes/15.0.0/" or "react-on-rails/docs/upgrading/release-notes/15.0.0"
const extractNames = (slug) => {
  const parts = slug.split("/").filter(p => p !== "") // Remove empty parts from trailing slash

  // Find where 'docs' is in the path
  const docsIndex = parts.indexOf('docs')

  if (docsIndex !== -1 && docsIndex < parts.length - 1) {
    // Everything after 'docs' except the last part is the folder path
    const articleMdxName = parts[parts.length - 1]
    const articleFolder = parts.slice(docsIndex + 1, -1).join("/")

    return [articleFolder, articleMdxName]
  }

  // Fallback for non-docs paths
  return ["", parts[parts.length - 1] || parts[0]]
}

const sortArticlesByFolder = (isReactOnRails: boolean) => (folders, currentArticle) => {
  const [articleFolder, articleMdxName] = extractNames(currentArticle.node.childMdx.fields.slug)

  // For nested folders like "upgrading/release-notes", use only first-level folder for category matching
  const firstLevelFolder = articleFolder ? articleFolder.split("/")[0] : ""
  const folderLabel = pageNametoLabel(firstLevelFolder)

  // Determine which folder this article belongs to
  let currentFolder
  if (indexPages.includes(articleMdxName) && articleFolder === "" && isReactOnRails) {
    // Special case: introduction.md in react-on-rails should appear under "Getting Started"
    currentFolder = folders.find(f => f.name === "Getting Started")
  } else if (articleFolder === "") {
    // Root-level files go into firstCategory (empty name)
    currentFolder = folders.find(f => f.name === "")
  } else {
    // Files in folders go into their respective category
    currentFolder = folders.find(f => f.name === folderLabel)
  }

  // Index pages (README.md, introduction.md, etc.) go to the front as "Overview"
  if (indexPages.includes(articleMdxName)) {
    currentFolder.pages.unshift(currentArticle)
  } else {
    currentFolder.pages.push(currentArticle)
  }

  return folders
}

const DocSidebar = ({ repoName, mdxName, currentSlug, articles, categories, isMobileMenu = false }: Props) => {
  const menuRef = useRef<HTMLDivElement>(null)
  const currentItemRef = useRef<HTMLLIElement>(null)

  // Only react-on-rails uses "Getting Started" for introduction.md
  const isReactOnRails = repoName === "react-on-rails"

  // Scroll to current item only if it's not already visible
  // Skip in mobile menu mode - parent MobileMenu handles scroll restoration
  useEffect(() => {
    if (!isMobileMenu && currentItemRef.current && typeof window !== 'undefined') {
      const element = currentItemRef.current as any
      if (typeof element.scrollIntoViewIfNeeded === 'function') {
        element.scrollIntoViewIfNeeded()
      } else {
        currentItemRef.current.scrollIntoView({ block: 'nearest', behavior: 'auto' })
      }
    }
  }, [currentSlug, isMobileMenu])

  // Organize each article into folder-category
  const allFoldersAndArticles = articles.edges.reduce(
    sortArticlesByFolder(isReactOnRails),
    categories.map(item => ({ name: item, pages: [] }))
  )

  // Sort pages within each folder according to fileOrderByRepo configuration
  const fileOrder = fileOrderByRepo[repoName]
  if (fileOrder) {
    allFoldersAndArticles.forEach(folder => {
      const folderKey = folder.name.toLowerCase().replace(/\s+/g, "-")
      const orderArray = fileOrder[folderKey]

      if (orderArray) {
        // Sort pages: index pages first, then ordered files, then unordered alphabetically
        folder.pages.sort((a, b) => {
          const [, aName] = extractNames(a.node.childMdx.fields.slug)
          const [, bName] = extractNames(b.node.childMdx.fields.slug)

          // Index pages always come first (they render as "Overview")
          const aIsIndex = indexPages.includes(aName)
          const bIsIndex = indexPages.includes(bName)
          if (aIsIndex && !bIsIndex) return -1
          if (!aIsIndex && bIsIndex) return 1

          const aIndex = orderArray.indexOf(aName)
          const bIndex = orderArray.indexOf(bName)

          // Both in order array - sort by their position in array
          if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex
          }

          // Only a is in order array - a comes first
          if (aIndex !== -1) {
            return -1
          }

          // Only b is in order array - b comes first
          if (bIndex !== -1) {
            return 1
          }

          // Neither in order array - sort alphabetically by label
          const aLabel = pageNametoLabel(aName)
          const bLabel = pageNametoLabel(bName)
          return aLabel.localeCompare(bLabel)
        })
      }
    })
  }

  const createLink = (articleMdxName, articleFolder, anchor = "") => {
    if (indexPages.includes(articleMdxName)) {
      if (articleFolder) {
        return `/${repoName}/docs/${articleFolder}/${anchor && `#${anchor}`}`
      }
      return `/${repoName}/docs/${anchor && `#${anchor}`}`
    }
    if (articleFolder === "") {
      return `/${repoName}/docs/${articleMdxName}/${anchor && `#${anchor}`}`
    }
    return `/${repoName}/docs/${articleFolder}/${articleMdxName}/${anchor && `#${anchor}`}`
  }

  return (
    <>
      <DocMenu ref={menuRef} menuOffset={baseMenuOffset}>
        {allFoldersAndArticles.map(item => (
            <>
              {item.name !== "" && (
                <CategoryTitle key={`title-${item.name}`}>
                  {item.name}
                </CategoryTitle>
              )}
              <ArticlesList key={`ul-${item.name}`}>
                {item.pages.map(article => {
                  const {slug} = article.node.childMdx.fields
                  const [articleFolder, articleMdxName] = extractNames(slug)
                  const label = pageNametoLabel(articleMdxName)
                  const isCurrent = slug === currentSlug

                  return (
                    <ArticlesListItem key={slug} ref={isCurrent ? currentItemRef : null}>
                      <StyledLink
                        isCurrent={isCurrent}
                        to={createLink(articleMdxName, articleFolder)}
                      >
                        {label}
                      </StyledLink>
                    </ArticlesListItem>
                  )
                })}
              </ArticlesList>
            </>
          ))}
        <Copyrights>
          <StyledIconShakacodeDoc />
          <p>Copyright {new Date().getFullYear()} ShakaCode</p>
        </Copyrights>
      </DocMenu>
    </>
  )
}

export default DocSidebar
